// Ship 7i.2 (2026-08-27) — CB-4 witness identity + service-detail resolver
// (frontend mirror of api/src/lib/witnessIdentity.js).
//
// The pre-code panel required ONE precedence rule enforced identically in the
// detail view, the API response, the designation preflight, and the PDF:
//
//     per-field witness override (nonblank, trimmed) → contact fallback → null
//
// The backend already attaches resolved_* to every witness row it returns, so
// the happy path here is just "read resolved_*".  We recompute locally only
// when handed a raw row (defensive — a future caller might build a row from a
// narrower query).  Recomputing uses the SAME ordering, so the two paths can
// never disagree.
//
// WHY THIS FLIPS SHIP 7i's ORDER: the old PDF helpers read
// `w.contact_address || w.address` — contact FIRST.  With no `address` column
// that was a no-op, but once the override column exists that ordering silently
// discards the service address the attorney typed on the witness row.  An
// expert routinely wants service at counsel's office rather than the home
// address on their contact card.  Override wins, always.

function blankToNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length === 0 ? null : s;
}

function contactAddressFrom(row) {
  const parts = [];
  const street = blankToNull(row.contact_address);
  if (street) parts.push(street);
  const cityLine = [
    blankToNull(row.contact_city),
    blankToNull(row.contact_state),
    blankToNull(row.contact_zip),
  ].filter(Boolean).join(', ');
  if (cityLine) parts.push(cityLine);
  return parts.length ? parts.join(', ') : null;
}

function contactNameFrom(row) {
  const first = blankToNull(row.contact_first_name) || blankToNull(row.first_name);
  const last = blankToNull(row.contact_last_name) || blankToNull(row.last_name);
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return blankToNull(row.contact_company_name) || blankToNull(row.company_name);
}

/**
 * resolveWitnessIdentity(row) → { name, address, phone, email, *_source }
 *
 * Prefers the backend-computed resolved_* fields; recomputes locally when they
 * are absent.  *_source is 'override' | 'contact' | null.
 */
export function resolveWitnessIdentity(row) {
  if (!row) {
    return {
      name: null, name_source: null,
      address: null, address_source: null,
      phone: null, phone_source: null,
      email: null, email_source: null,
    };
  }

  // Fast path — backend already resolved.  `resolved_name_source` being
  // present (even as null) means the backend ran the resolver on this row.
  if ('resolved_name_source' in row) {
    return {
      name: row.resolved_name ?? null,
      name_source: row.resolved_name_source ?? null,
      address: row.resolved_address ?? null,
      address_source: row.resolved_address_source ?? null,
      phone: row.resolved_phone ?? null,
      phone_source: row.resolved_phone_source ?? null,
      email: row.resolved_email ?? null,
      email_source: row.resolved_email_source ?? null,
    };
  }

  const nameOverride = blankToNull(row.display_name);
  const nameContact = contactNameFrom(row);
  const name = nameOverride || nameContact || null;

  const addrOverride = blankToNull(row.address);
  const addrContact = contactAddressFrom(row);
  const address = addrOverride || addrContact || null;

  const phoneOverride = blankToNull(row.phone);
  const phoneContact = blankToNull(row.contact_phone);
  const phone = phoneOverride || phoneContact || null;

  const emailOverride = blankToNull(row.email);
  const emailContact = blankToNull(row.contact_email);
  const email = emailOverride || emailContact || null;

  return {
    name, name_source: nameOverride ? 'override' : (nameContact ? 'contact' : null),
    address, address_source: addrOverride ? 'override' : (addrContact ? 'contact' : null),
    phone, phone_source: phoneOverride ? 'override' : (phoneContact ? 'contact' : null),
    email, email_source: emailOverride ? 'override' : (emailContact ? 'contact' : null),
  };
}

/** Name for display, never blank — court filings must not render an empty name. */
export function witnessDisplayName(row) {
  return resolveWitnessIdentity(row).name || '(unnamed witness)';
}

/** Resolved service address, or null when neither side has one. */
export function witnessServiceAddress(row) {
  return resolveWitnessIdentity(row).address;
}

/** Resolved phone, or null. */
export function witnessPhone(row) {
  return resolveWitnessIdentity(row).phone;
}

/** Resolved email, or null. */
export function witnessEmail(row) {
  return resolveWitnessIdentity(row).email;
}

export { blankToNull };
